import { motion } from 'framer-motion';
import { Heart, Award, MapPin, Users, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: Heart,
    title: 'Compassion',
    description: 'We believe everyone deserves access to life-saving skills.',
  },
  {
    icon: Users,
    title: 'Community',
    description: 'Serving families, churches, businesses, and organizations.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'Professional, certified instruction you can trust.',
  },
  {
    icon: MapPin,
    title: 'Accessibility',
    description: 'Mobile services that come to you, on your schedule.',
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EDF6F9] via-white to-[#83C5BE]/20 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                <span>About Us</span>
              </div>
              <h1 
                className="text-4xl md:text-5xl font-bold text-[#264653] mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="about-title"
              >
                Because Every Breath Matters
              </h1>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                LifeBreath CPR & First Aid exists to equip individuals, families, churches, 
                and organizations with life-saving skills through accessible, mobile training—empowering 
                communities to respond with confidence, compassion, and care when every breath matters.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1659353885824-1199aeeebfc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxtZWRpY2FsJTIwaW5zdHJ1Y3RvciUyMHRlYWNoaW5nJTIwY2xhc3MlMjBzbWlsaW5nfGVufDB8fHx8MTc2OTkxNDY1OHww&ixlib=rb-4.1.0&q=85"
                  alt="Medical professional - CPR & First Aid Instructor"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#264653]/30 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#264653] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Our Mission
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              We're dedicated to making life-saving training accessible to everyone in our community. 
              Whether you're a parent wanting to protect your family, a business owner looking to 
              train your staff, or a caregiver needing certification, we bring professional 
              training directly to you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">AHA Certified</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#83C5BE]/20 text-[#006D77]">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Mobile Services</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#E29578]/20 text-[#E29578]">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Flexible Scheduling</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-[#EDF6F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#264653] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Our Values
            </h2>
            <p className="text-muted-foreground">What drives everything we do</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full text-center border-slate-100 hover:shadow-lg transition-all" data-testid={`value-card-${index}`}>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 
                      className="text-xl font-semibold text-[#264653] mb-3"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#264653] mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Serving Georgia
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                We provide mobile CPR/First Aid training and notary services throughout Georgia. 
                Our flexible scheduling means we work around your availability—whether that's 
                weekdays, evenings, or weekends.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-[#264653]">On-site training at your home, office, church, or facility</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-[#264653]">Evening and weekend appointments available</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-[#264653]">Group discounts for organizations</span>
                </li>
              </ul>
              <Link to="/book-now">
                <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="about-book-btn">
                  Book Now
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="bg-[#EDF6F9] border-0 p-8" data-testid="contact-card">
                <CardContent className="p-0 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Call or Text</p>
                      <a href="tel:912-388-6305" className="font-bold text-xl text-[#264653]">
                        912-388-6305
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#83C5BE]/20 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#006D77]" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Service Area</p>
                      <p className="font-bold text-xl text-[#264653]">Georgia-Wide</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#E29578]/20 flex items-center justify-center">
                      <Award className="w-6 h-6 text-[#E29578]" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Certification</p>
                      <p className="font-bold text-xl text-[#264653]">AHA Certified</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
